import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { Button, Card } from '@/shared/ui';
import { getDB } from '@/shared/storage';
import { jobScheduler } from '@/shared/jobs';
import { isTerminalJobStatus } from '@/entities/job';

export function BrandListPage() {
  const brands = useLiveQuery(() => getDB().brands.orderBy('createdAt').reverse().toArray(), []);

  async function deleteBrand(brandId: string, name: string) {
    if (!confirm(`Delete "${name}"? Generated images, jobs and product images for this brand will also be removed.`)) {
      return;
    }
    const db = getDB();
    const inFlight = await db.jobs
      .where('brandId')
      .equals(brandId)
      .filter((j) => !isTerminalJobStatus(j.status))
      .toArray();
    await Promise.all(inFlight.map((j) => jobScheduler.cancel(j.id)));
    await db.transaction(
      'rw',
      [db.brands, db.jobs, db.productImages, db.generatedImages, db.resolvedPrompts],
      async () => {
        const jobs = await db.jobs.where('brandId').equals(brandId).toArray();
        const jobIds = jobs.map((j) => j.id);
        if (jobIds.length > 0) {
          await db.generatedImages.where('jobId').anyOf(jobIds).delete();
          await db.resolvedPrompts.where('jobId').anyOf(jobIds).delete();
          await db.jobs.bulkDelete(jobIds);
        }
        await db.productImages.where('brandId').equals(brandId).delete();
        await db.brands.delete(brandId);
      },
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Brands</h1>
        <Link to="/brands/new">
          <Button>New brand</Button>
        </Link>
      </div>
      {(brands ?? []).length === 0 ? (
        <p className="text-sm text-muted-foreground">No brands yet. Start with a new brand.</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {(brands ?? []).map((b) => (
            <Card key={b.id} className="group relative transition-colors hover:bg-muted/40">
              <Link to="/brands/$brandId" params={{ brandId: b.id }} className="block">
                <div className="font-medium">{b.name}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {b.url ?? <span className="italic">no URL</span>}
                </div>
              </Link>
              <button
                type="button"
                onClick={() => void deleteBrand(b.id, b.name)}
                className="absolute right-2 top-2 rounded bg-black/50 px-2 py-0.5 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100"
              >
                Delete
              </button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
